import { useMemo } from 'react';
import { slugify } from '@surfline/web-common';
import { createParamString } from 'common/baseFetch';
import { spotReportPaths } from 'utils/urls/spotReportPath';
import { oneHour } from 'utils/time';
import { Player } from 'components/CamPlayerKbygRewinds/usePlayerState/usePlayerState';
import { SpotDetails } from 'types/spot';
import { Camera } from 'types/camera';

interface ErrorState {
  message: string;
  subMessage?: string | null;
}

interface UseClipsButtonProps {
  camera: Camera;
  spot?: SpotDetails;
  currentPreviewPlayer: Player;
  currentTimestamp?: Date | null;
  nextAvailableRewindFromLive?: string | null;
  bucketEndTimeInMs: number | null;
  hasCamRewindPermissions: boolean;
  rewindPlayerError: ErrorState | null;
  enabledFeatures?: {
    wavePool?: boolean;
  };
  adRunning?: boolean;
  isNative: boolean;
}

interface UseClipsButtonReturn {
  showClipsPrompt: boolean;
  clipButtonDisabled: boolean;
  clipsButtonUrl: string;
}

const useClipsButton = ({
  camera,
  spot,
  currentPreviewPlayer,
  currentTimestamp,
  nextAvailableRewindFromLive,
  bucketEndTimeInMs,
  hasCamRewindPermissions,
  rewindPlayerError,
  enabledFeatures = {},
  adRunning,
  isNative,
}: UseClipsButtonProps): UseClipsButtonReturn => {
  const showClipsPrompt = useMemo(() => {
    const now = new Date().getTime();

    return Boolean(
      hasCamRewindPermissions &&
        bucketEndTimeInMs &&
        now - bucketEndTimeInMs > oneHour &&
        !adRunning,
    );
  }, [bucketEndTimeInMs, hasCamRewindPermissions, adRunning]);

  const currentTime = useMemo(() => {
    let time = null;
    if (currentPreviewPlayer !== Player.LIVE) {
      time = currentTimestamp?.getTime();
    } else if (currentPreviewPlayer === Player.LIVE && nextAvailableRewindFromLive) {
      time = new Date(nextAvailableRewindFromLive).getTime();
    }
    return time;
  }, [currentPreviewPlayer, currentTimestamp, nextAvailableRewindFromLive]);

  const clipButtonDisabled = !(spot?.name && spot?._id && !rewindPlayerError && currentTime);

  const baseUrl = enabledFeatures.wavePool ? spotReportPaths.baseWavePool : spotReportPaths.base;

  const clipsButtonUrl = useMemo(
    () =>
      !clipButtonDisabled
        ? `${baseUrl}/${slugify(spot?.name)}/${spot?._id}/clips?${createParamString({
            currentTimestamp: currentTime,
            camId: camera?._id,
            ...(isNative && { native: true }),
          })}`
        : '',
    [camera?._id, clipButtonDisabled, currentTime, baseUrl, spot?._id, spot?.name, isNative],
  );

  return {
    showClipsPrompt,
    clipButtonDisabled,
    clipsButtonUrl,
  };
};

export default useClipsButton;
